import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  ArrowRight,
  CheckCircle,
  Clock,
  MapPin,
  ShieldCheck,
  Sparkles,
  Calendar,
  CreditCard,
  Users,
  Zap,
  Brain,
  Shield,
  Heart,
  HeartHandshake,
} from "lucide-react";
import SEO from "@/components/SEO";
import { usePageSeo } from "@/hooks/usePageSeo";
import StoriesOfHealing from "@/components/StoriesOfHealing";
import WhatIsKAP from "@/components/WhatIsKAP";
import PracticeModelInvestment from "@/components/PracticeModelInvestment";
import CalendlyInlineBooking from "@/components/CalendlyInlineBooking";
import KetamineSavingsPlan from "@/components/KetamineSavingsPlan";
import CareCreditBanner from "@/components/CareCreditBanner";
import { GroupKapNarrative } from "@/components/GroupKapNarrative";

const LAURA_IMAGE =
  "https://customer-assets.emergentagent.com/job_833ff4cd-a791-443f-9fd4-023fdfa8f8c0/artifacts/l4ja8zkq_IMG_4558_Facetune_17-01-2023-21-22-22.jpeg";

// Warm, symbolic connection/reaching-hands image evoking group healing.
const GROUP_IMAGE =
  "https://images.unsplash.com/photo-1544027993-37dbfe43562a?w=1600&q=80&auto=format&fit=crop";

const PACKAGE_ITEMS = [
  "Individual Diagnostic Evaluation Session — 1 hour",
  "Individual Treatment Planning Session — 1 hour",
  "Group Introduction & Preparation Session — 1 hr 30 min",
  "(2) Group Ketamine Dosing Sessions — 2.5 hours each",
  "(2) Group Processing Sessions — 2 hours each",
  "Group Graduation Session — 1 hr 30 min",
  "Individual Graduation Session — 30 min",
];

const BENEFITS = [
  {
    icon: Users,
    title: "You’re Not Alone",
    description:
      "Sit in circle with women who understand hyper-independence from the inside. The isolation you’ve carried gets held by others who’ve been there too.",
  },
  {
    icon: Zap,
    title: "Rapid Relief",
    description:
      "Ketamine helps many clients feel significant shifts within hours to days — no more waiting weeks for antidepressants to maybe work.",
  },
  {
    icon: Brain,
    title: "Neuroplasticity Enhancement",
    description:
      "Ketamine works with NMDA receptors to create new neural pathways, helping your brain break free from the patterns that kept you armored up.",
  },
  {
    icon: Heart,
    title: "Deep Emotional Processing",
    description:
      "The therapeutic window opens space to feel what you’ve been avoiding — and process it with a witness, not in isolation.",
  },
  {
    icon: Shield,
    title: "Treatment-Resistant Support",
    description:
      "If years of talk therapy or medication haven’t moved the needle, KAP often reaches the places words alone couldn’t.",
  },
  {
    icon: HeartHandshake,
    title: "Learning To Receive",
    description:
      "In group, you practice the exact thing hyper-independent women struggle with most: letting yourself be seen, held, and cared for.",
  },
];

const DEFAULT_DESCRIPTION =
  "A 9-week Virtual Ketamine-Assisted Therapy Group Immersion for hyper-independent women in Florida and Maine. 14.5 clinical hours over 6 extended group sessions — $2,500. Book your free consultation with Laura A. Jeanis, LCSW.";
const DEFAULT_IMAGE_ALT =
  "Laura A. Jeanis, LCSW, LICSW, CCTP — Virtual Ketamine-Assisted Therapy Group Immersion for hyper-independent women in FL and ME";

const SpecialtyGroupImmersionPage = () => {
  const seoOverride = usePageSeo("group-immersion");
  const description = seoOverride?.meta_description || DEFAULT_DESCRIPTION;
  const imageAlt = seoOverride?.image_alt || DEFAULT_IMAGE_ALT;

  return (
    <div className="pt-20" data-testid="specialty-group-immersion-page">
      <SEO
        metaTitle={seoOverride?.meta_title}
        keywords={[seoOverride?.focus_keyword, ...(seoOverride?.secondary_keywords || [])]}
        title="Virtual Ketamine-Assisted Therapy 9-Week Group Immersion Intensive | For Hyper-Independent Women in FL & ME"
        description={description}
        image={LAURA_IMAGE}
        imageAlt={imageAlt}
        pathname="/specialties/ketamine-therapy-group-immersion-fl-me"
      />

      {/* === HERO === */}
      <section className="py-20 md:py-28 px-6 md:px-12 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex justify-center mb-8"
          >
            <span
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium"
              data-testid="group-location-pill"
            >
              <MapPin className="h-4 w-4" />
              For FL &amp; ME Residents — fully online
            </span>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 items-start">
            {/* Group image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-2 flex justify-center lg:justify-start lg:sticky lg:top-28"
            >
              <div className="relative">
                <div className="absolute -inset-3 rounded-3xl bg-primary/10 -z-10"></div>
                <img
                  src={GROUP_IMAGE}
                  alt="Two hands reaching toward each other — a symbol of connection and shared healing in the Virtual Ketamine-Assisted Therapy Group Immersion for hyper-independent women"
                  className="rounded-3xl shadow-soft object-cover w-full max-w-md aspect-[4/5]"
                  data-testid="group-hero-image"
                />
              </div>
            </motion.div>

            {/* Title + eligibility */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="lg:col-span-3"
            >
              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-tight">
                Virtual Ketamine-Assisted Therapy{" "}
                <span className="italic text-primary">9 Weeks Group Immersion Intensive</span>{" "}
                | For Hyper-Independent Women Who Want To Finally Feel Safe In Their Relationships
              </h1>
              <GroupKapNarrative />
              <div
                className="mt-6 rounded-2xl bg-accent/10 border border-accent/30 p-4 flex items-start gap-3"
                data-testid="group-eligibility-callout"
              >
                <ShieldCheck className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                <h2 className="font-serif text-lg sm:text-xl font-semibold text-foreground leading-snug m-0">
                  You must live in Florida or Maine to qualify.
                </h2>
              </div>

              <div className="mt-6 space-y-2" data-testid="group-credentials">
                <p className="font-serif text-xl font-semibold text-foreground">
                  Laura A. Jeanis, LCSW, LICSW, CCTP, CEO
                </p>
                <p className="text-base text-muted-foreground leading-relaxed">
                  Licensed Psychotherapist · Certified Clinical Trauma Professional · 11+ years of clinical experience
                </p>
              </div>

              <div className="mt-10 flex flex-wrap gap-4">
                <a
                  href="https://calendly.com/divinelovewellnessenterprises/consultationmeeting"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    className="rounded-full px-8 py-6 text-base bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="group-hero-cta"
                  >
                    Book Your Free Consultation
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
                <Link to="/contact">
                  <Button
                    variant="outline"
                    className="rounded-full px-8 py-6 text-base border-primary/30 text-primary hover:bg-primary/5"
                  >
                    Ask Laura a Question
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* === Join the Next Cohort CTA Strip === */}
      <section
        className="relative overflow-hidden px-6 md:px-12 py-16 md:py-20 bg-gradient-to-br from-primary via-primary to-primary/90"
        data-testid="group-cohort-cta-strip"
      >
        {/* Decorative accents */}
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-accent/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-white/10 blur-3xl pointer-events-none" />

        <div className="relative max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8"
          >
            {/* Left: copy */}
            <div className="flex-1 text-primary-foreground">
              <span
                className="inline-flex items-center gap-2 rounded-full bg-accent/90 text-accent-foreground px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em]"
                data-testid="group-cohort-badge"
              >
                <Sparkles className="h-3.5 w-3.5" />
                Limited Seats · Next Cohort Enrolling
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold mt-5 leading-tight">
                Join the Next Cohort
              </h2>
              <p className="mt-4 text-base md:text-lg text-primary-foreground/90 leading-relaxed max-w-2xl">
                Each cohort is intentionally small so every woman is seen, held, and heard. Seats
                fill fast between live rounds — reserve yours now and we&rsquo;ll walk you through
                dates, eligibility, and payment plans on your free 40-minute consultation.
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-6 text-sm md:text-base text-primary-foreground/90">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-accent" />
                  Intimate group · women-only
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-accent" />
                  9 weeks · fully virtual
                </div>
                <div className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-accent" />
                  Payment plans from $125 bi-weekly
                </div>
              </div>
            </div>

            {/* Right: CTAs */}
            <div className="flex flex-col gap-3 lg:w-72 lg:flex-shrink-0">
              <Button
                asChild
                size="lg"
                className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 shadow-lg font-semibold"
                data-testid="group-cohort-cta-primary"
              >
                <Link to="/contact">
                  Reserve Your Seat
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="rounded-full border-primary-foreground/40 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
                data-testid="group-cohort-cta-secondary"
              >
                <Link to="/contact">Book a Free 40-min Consult</Link>
              </Button>
              <p className="text-xs text-center text-primary-foreground/70 mt-1">
                No commitment · 100% confidential
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* === Package Details === */}
      <section className="py-20 md:py-24 px-6 md:px-12 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="inline-flex items-center gap-2 text-primary font-medium text-sm uppercase tracking-[0.18em]">
              <Sparkles className="h-4 w-4" />
              What&rsquo;s Included
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-foreground mt-4 leading-tight">
              9 Weeks · 6 Extended Group Sessions · Held in Circle
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              A carefully sequenced container so you never have to navigate this alone.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            data-testid="group-package-card"
          >
            <Card className="p-8 md:p-12 bg-white border border-primary/20 rounded-3xl shadow-soft relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-primary to-accent" />

              <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
                <div>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                    <Users className="h-3.5 w-3.5" />
                    Group Immersion · Max 3 Members
                  </span>
                  <h3 className="font-serif text-2xl md:text-3xl font-semibold text-foreground mt-3">
                    9-Week Virtual KAP Group Immersion
                  </h3>
                  <p className="text-sm text-muted-foreground italic mt-2 leading-relaxed">
                    For the hyper-independent woman ready to lay her armor down — together with a small circle of women who&rsquo;ve been there too.
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Payment plans from $125 bi-weekly</p>
                </div>
              </div>

              <ul className="space-y-4 text-base text-foreground/90 mb-8">
                {PACKAGE_ITEMS.map((item) => (
                  <li key={item} className="flex items-start gap-3" data-testid="group-package-item">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-6 border-t border-border/40">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-full bg-accent/15 flex-shrink-0">
                    <Clock className="h-4 w-4 text-accent" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
                      Clinical Hours
                    </p>
                    <p className="font-serif text-lg font-semibold text-foreground">14.5 hours</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-full bg-accent/15 flex-shrink-0">
                    <Calendar className="h-4 w-4 text-accent" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
                      Commitment
                    </p>
                    <p className="font-serif text-lg font-semibold text-foreground">9 weeks</p>
                  </div>
                </div>
              </div>

              <a
                href="https://calendly.com/divinelovewellnessenterprises/consultationmeeting"
                target="_blank"
                rel="noopener noreferrer"
                className="block mt-8"
              >
                <Button
                  className="w-full rounded-full py-6 text-base bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="group-package-cta"
                >
                  Schedule a Free 40-Minute Consultation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
            </Card>
          </motion.div>

          {/* Savings callout */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-8"
            data-testid="group-savings-callout"
          >
            <div className="rounded-2xl bg-accent/10 border border-accent/30 p-5 flex items-start gap-3">
              <Sparkles className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
              <p className="text-sm text-foreground/90 leading-relaxed">
                <span className="font-semibold text-foreground">14.5 clinical hours across 6 extended group sessions + 3 individual sessions</span>{" "}
                — and it costs less than the first-tier individual intensive. A softer, more affordable entry point without giving up depth.
              </p>
            </div>
          </motion.div>

          {/* Continuation package callout */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="mt-6"
            data-testid="group-continuation-callout"
          >
            <Link to="/fees" className="group block">
              <div className="rounded-2xl bg-primary/5 border border-primary/20 p-5 flex items-start gap-3 hover:border-primary/40 hover:bg-primary/10 transition-all">
                <ArrowRight className="h-5 w-5 text-primary flex-shrink-0 mt-0.5 group-hover:translate-x-1 transition-transform" />
                <p className="text-sm text-foreground/90 leading-relaxed">
                  <span className="font-semibold text-foreground">Do you want to go deeper?</span>{" "}
                  Check out our continuation intensive for your individualized journey!
                </p>
              </div>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* === CareCredit Financing Banner === */}
      <CareCreditBanner variant="muted" />

      {/* === Practice Model & Investment === */}
      <PracticeModelInvestment background="bg-white" />

      {/* === Benefits === */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-secondary/30" data-testid="group-benefits-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-foreground">
              Why Group Ketamine-Assisted Psychotherapy?
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
              The medicine goes deep. The circle goes even deeper.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {BENEFITS.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="p-8 h-full bg-white border-0 rounded-2xl shadow-sm hover:shadow-soft transition-shadow">
                  <div className="p-3 rounded-full bg-primary/10 w-fit mb-4">
                    <benefit.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-foreground mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {benefit.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* === What is KAP? === */}
      <WhatIsKAP background="bg-secondary/30" />

      {/* === Stories of Healing === */}
      <StoriesOfHealing background="bg-white" />

      {/* === Ketamine Intensive Savings Plan — teaser (canonical article lives on /fees) === */}
      <KetamineSavingsPlan background="bg-white" variant="teaser" />

      {/* === Paired & Dual-Journey Healing === */}
      <section
        className="py-20 md:py-28 px-6 md:px-12 bg-secondary/30"
        data-testid="paired-healing-section"
      >
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold text-center mb-4">
              Paired &amp; Dual-Journey Healing
            </p>
            <h3 className="font-serif text-2xl sm:text-3xl md:text-4xl font-semibold text-foreground text-center leading-tight">
              Virtual Group Cohorts Across FL &amp; ME
            </h3>
            <p className="mt-6 font-serif italic text-lg text-primary text-center">
              Interested in Group or Paired Healing?
            </p>
            <div className="mt-8 space-y-5 text-base md:text-lg text-foreground/80 leading-[1.85]">
              <p>
                Healing relational trauma and hyper-independence doesn&rsquo;t have to be a solo journey. Bring a trusted friend, partner, or associate to embark on a synchronized, dual Ketamine-Assisted Psychotherapy (KAP) intensive or somatic healing journey with you.
              </p>
              <p>
                Doing this work alongside a peer deepens nervous system coregulation, breaks down emotional isolation, and provides a shared language for long-term integration &mdash; all within a private, culturally attuned, and clinically grounded space.
              </p>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-5 bg-white border border-border/40 rounded-2xl">
                <p className="text-xs uppercase tracking-wide text-primary font-semibold">Format</p>
                <p className="mt-2 font-serif text-base font-semibold text-foreground leading-snug">
                  Virtual Paired Intensives &amp; Small Synchronized Journeys
                </p>
              </div>
              <div className="p-5 bg-white border border-border/40 rounded-2xl">
                <p className="text-xs uppercase tracking-wide text-primary font-semibold">Service Areas</p>
                <p className="mt-2 font-serif text-base font-semibold text-foreground leading-snug">
                  Available to residents throughout Florida and Maine
                </p>
              </div>
              <div className="p-5 bg-white border border-border/40 rounded-2xl">
                <p className="text-xs uppercase tracking-wide text-primary font-semibold">Investment</p>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  Private pay with custom intensive arrangements, extended payment plans, and out-of-network insurance reimbursement filing available via Thrizer.
                </p>
              </div>
            </div>

            <p className="mt-10 text-center text-base md:text-lg text-foreground/80 leading-relaxed">
              Ready to journey together?{" "}
              <Link to="/contact" className="text-primary font-semibold underline underline-offset-4 decoration-primary/40 hover:decoration-primary">
                Contact us today
              </Link>{" "}
              to discuss paired intake options and structure a synchronized intensive that fits your shared timeline.
            </p>
          </motion.div>
        </div>
      </section>

      {/* === Inline Calendly Booking Widget === */}
      <CalendlyInlineBooking background="bg-secondary/30" />

      {/* === Closing CTA === */}
      <section className="py-20 md:py-24 px-6 md:px-12 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-foreground leading-tight">
              Ready to heal in circle — not alone?
            </h2>
            <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
              Book a free 40-minute consultation with Laura. We&rsquo;ll talk about whether the 9-week Group Immersion is the right container for you.
            </p>
            <div className="mt-10">
              <a
                href="https://calendly.com/divinelovewellnessenterprises/consultationmeeting"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  className="rounded-full px-10 py-7 text-lg bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="group-closing-cta"
                >
                  Book Your Free Consultation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SpecialtyGroupImmersionPage;
